import { Injectable } from '@angular/core';
import { LoggerService } from '../../../core/services/logger.service';
import { UtilService } from '../../../core/services/util.service';
import {
  CIFDatadogService,
  CloseTicketByEventProps,
} from 'src/app/shared/services/cif-datadog';
import { TicketCreationRequestControlService } from 'src/app/shared/services/ticket-creation-request-control.service';
import { MediaType } from 'src/app/utils/media-type';
import { isDigital } from 'src/app/utils/cxone-utils';
import { JsonConverterUtils } from 'src/app/data/CXOne/util/JsonConverterUtils';

export type Ticket = { id_interacao: string | null; id_ticket: string | null };

export interface TemplateValuesChannelProvider {
  fieldNegocio: string;
  triggerPlugin: string;
  templateAusente: string;
  templateSessaoPadrao: string;
  templateCreateSession: string;
  retrieveRecordTable: string;
  retrieveRecordCol: string;
}

@Injectable({
  providedIn: 'root',
})
export class DynamicsIbbaService {
  private readonly RECALL_LIMIT = 30;
  private readonly RECALL_DELAY_MS = 2000;
  private readonly tableName: string = 'itau_interacao';
  private readonly retrieveRecordTable =
    'itau_incident_InteracaoId_itau_interacao';
  private readonly retrieveRecordCol = 'incidentid';
  public templateValuesChannelProvider: TemplateValuesChannelProvider;

  constructor(
    private logger: LoggerService,
    private utilService: UtilService,
    private cifDatadogService: CIFDatadogService,
    private ticketCreationRequestControlService: TicketCreationRequestControlService,
    private jsonConverterUtils: JsonConverterUtils,
  ) {
    this.templateValuesChannelProvider = {
      fieldNegocio: 'itau_name', //OK
      triggerPlugin: 'jw7_st_plugin', //OK
      templateAusente: 'jw7_nice_ausente', //OK
      templateSessaoPadrao: 'jw7_ibba_guia_ancora', //OK
      templateCreateSession: 'jw7_ibba_guia_nice', //OK
      retrieveRecordTable: 'itau_incident_InteracaoId_itau_interacao', //OK
      retrieveRecordCol: 'incidentid', //OK
    };
  }

  async initialize(cifUrl: string): Promise<void> {
    this.logger.info(`DYNAMICS - inicializando CIF: ${cifUrl}`);
    this.utilService.getIframeEnvironment();
    const script = document.createElement('script');

    script.src = cifUrl;
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('data-crmurl', cifUrl);
    script.setAttribute('data-cifid', 'CIFMainLibrary');

    document.body.appendChild(script);

    return new Promise<void>((resolve, reject) => {
      const onCIFInitDone = async (event: Event) => {
        this.logger.info('DYNAMICS - CIF inicializado', event);
        try {
          this.logger.info('DYNAMICS - CIF Iframe setado para 400 pixels');

          await (window as any).Microsoft.CIFramework.setMode(1);

          await (window as any).Microsoft.CIFramework.addHandler(
            'onmodechanged',
            async (eventData: any) => {
              this.logger.info('DYNAMICS - CIF Mode Changed:', eventData);

              await (window as any).Microsoft.CIFramework.setMode(1);
            },
          );

          await (window as any).Microsoft.CIFramework.setWidth(400);
        } catch (error) {
          this.logger.error('DYNAMICS - Erro ao inicializar CIF', error);
          reject(error);
        } finally {
          resolve();
          window.removeEventListener('CIFInitDone', onCIFInitDone);
        }
      };

      window.addEventListener('CIFInitDone', onCIFInitDone);
    });
  }

  public async loadTemplateValues(): Promise<TemplateValuesChannelProvider | null> {
    const channelProviderVariablesRaw = await (
      window as any
    ).Microsoft.CIFramework.getEnvironment();

    if (channelProviderVariablesRaw) {
      //const channelProviderVariables = JSON.parse(channelProviderVariablesRaw);
      let channelProviderVariables: any = {};

      // try {
      //   channelProviderVariables = JSON.parse(channelProviderVariablesRaw);
      // } catch {
      //   channelProviderVariables = {};
      // }

      channelProviderVariables = this.jsonConverterUtils.JsonParsed(
        channelProviderVariablesRaw,
      );

      let customParams: any = {};
      if (channelProviderVariables.msdyn_customparams) {
        customParams = Object.fromEntries(
          channelProviderVariables.msdyn_customparams
            .split('&')
            .map((kv: string) => kv.split('=')),
        );
      }

      const templateValues: TemplateValuesChannelProvider = {
        templateCreateSession:
          customParams.templateCreateSession ||
          this.templateValuesChannelProvider.templateCreateSession,
        templateSessaoPadrao:
          customParams.templateSessaoPadrao ||
          this.templateValuesChannelProvider.templateSessaoPadrao,
        templateAusente:
          customParams.templateAusente ||
          this.templateValuesChannelProvider.templateAusente,
        fieldNegocio:
          customParams.fieldNegocio ||
          this.templateValuesChannelProvider.fieldNegocio,
        triggerPlugin:
          customParams.triggerPlugin ||
          this.templateValuesChannelProvider.triggerPlugin,
        retrieveRecordTable:
          customParams.retrieveRecordTable ||
          this.templateValuesChannelProvider.retrieveRecordTable,
        retrieveRecordCol:
          customParams.retrieveRecordCol ||
          this.templateValuesChannelProvider.retrieveRecordCol,
      };

      this.templateValuesChannelProvider = templateValues;

      this.logger.info('DYNAMICS - Template Values Loaded:', templateValues);

      return templateValues;
    } else {
      this.logger.error(
        'Erro ao carregar environment do CIFramework:',
        channelProviderVariablesRaw,
      );
      return null;
    }
  }

  public async cifCreateSession(
    input_template: any,
    mediaType: MediaType | null,
    masterId: string,
  ): Promise<string> {
    let interactionId = null;
    try {
      interactionId = input_template['templateParameters']?.id_interacao;
      this.cifDatadogService.openTicket.session.request({
        interactionId,
        template: input_template,
        mediaType,
        masterId,
      });

      const canCreate = await (
        window as any
      ).Microsoft.CIFramework.canCreateSession();
      if (canCreate) {
        const sessionId = await (
          window as any
        ).Microsoft.CIFramework.createSession(input_template);

        this.logger.info('DYNAMICS - sessionId do createSession', sessionId);

        this.cifDatadogService.openTicket.session.success({
          sessionId: sessionId,
          interactionId: interactionId,
          mediaType,
          masterId,
        });

        return sessionId;
      }

      this.logger.warn(
        'DYNAMICS - Não é possível criar uma sessão com o template fornecido.',
      );
      throw new Error();
    } catch (error: any) {
      this.cifDatadogService.openTicket.session.fail({
        interactionId,
        template: input_template,
        mediaType,
        masterId,
        error,
      });
      this.logger.error('DYNAMICS - Erro ao criar sessão', error);
      throw error; // Re-throw the error to be caught by the caller
    }
  }

  public async cifCloseSession(
    input_template: string,
    context: any,
    sessionId: string,
    contactId: string,
    mediaType: MediaType,
  ): Promise<void> {
    let idInteracao = null;
    try {
      const updateResult = await (
        window as any
      ).Microsoft.CIFramework.updateContext(context, sessionId);

      const input = {
        templateName: input_template,
        templateParameters: {},
      };

      idInteracao = updateResult?.id_interacao;

      if (!sessionId || !idInteracao) {
        this.logger.warn(
          'IBBA - sessionId ou idInteracao ausentes, não é possível fechar a sessão.',
        );
        return;
      }

      await (window as any).Microsoft.CIFramework.notifyEvent(input);

      // Chama a função para motivar o encerramento com o id da interação
      const eventInputParameters = {
        sessionId: sessionId,
        correlationId: idInteracao,
      };

      const closeEventLogDetails: CloseTicketByEventProps = {
        sessionId: sessionId,
        interactionId: idInteracao,
        mediaType,
        masterId: contactId,
      };

      this.cifDatadogService.closeTicket.start(
        'fecharSessao',
        closeEventLogDetails,
      );

      await (window as any).Microsoft.CIFramework.raiseEvent(
        'fecharSessao',
        eventInputParameters,
      );

      this.cifDatadogService.closeTicket.success(
        'fecharSessao',
        closeEventLogDetails,
      );

      this.logger.info('IBBA - sessão fechada com sucesso', sessionId);
    } catch (error) {
      this.logger.error(
        'IBBA - Erro ao fechar sessão: ',
        error,
        'sessionId:',
        sessionId,
      );

      this.cifDatadogService.closeTicket.fail('fecharSessao', {
        sessionId: sessionId,
        interactionId: idInteracao,
        error,
        mediaType,
        masterId: contactId,
      });
      throw error;
    }
  }

  public async cifCreateTicket(
    attachData: any,
    uJSON: unknown,
    contactId: string,
    mediaType: MediaType,
  ): Promise<Ticket> {
    let retorno: Ticket = {
      id_interacao: null,
      id_ticket: null,
    };
    this.cifDatadogService.openTicket.initialData({
      attachData,
      masterId: contactId,
      mediaType,
    });

    if (isDigital(mediaType)) {
      const ticketHasRequest =
        this.ticketCreationRequestControlService.checkTicketRequest(
          attachData.itau_st_id_conexao,
        );

      if (ticketHasRequest === true) {
        this.logger.info(
          'DYNAMICS - Master ID já inserido na lista, ',
          attachData.itau_st_id_conexao,
        );
        this.cifDatadogService.openTicket.request.duplicated({
          masterId: contactId,
          attachData: uJSON,
          mediaType,
        });
        return retorno;
      }

      this.cifDatadogService.openTicket.request.create({
        attachData: uJSON,
        mediaType,
        masterId: contactId,
      });

      this.ticketCreationRequestControlService.saveTicketRequest(
        attachData.itau_st_id_conexao,
      );
      this.ticketCreationRequestControlService.clearUpLastTicketCreationRequest();
    }

    try {
      const result = await (window as any).Microsoft.CIFramework.createRecord(
        this.tableName,
        JSON.stringify(attachData),
      );

      this.cifDatadogService.openTicket.request.success({
        attachData: uJSON,
        mediaType,
        masterId: contactId,
      });

      this.logger.info(`DYNAMICS - ID Interação criado: ${result}`);
      let id_interacao = '';

      // try {
      //   id_interacao = JSON.parse(result).id;
      // } catch {
      //   id_interacao = '';
      // }

      id_interacao = this.jsonConverterUtils.JsonParsed(result).id;

      //retorno.id_interacao = JSON.parse(result).id;
      retorno.id_interacao = id_interacao;

      //recuperacao do ID do ticket
      if (retorno.id_ticket == null && retorno.id_interacao != null) {
        retorno.id_ticket = await this.cifRetrieveRecord(
          retorno.id_interacao,
          this.tableName,
          mediaType,
          contactId,
        );
        this.logger.info(`DYNAMICS - Ticket recuperado: ${retorno.id_ticket}`);
      }
    } catch (error) {
      this.cifDatadogService.openTicket.request.fail({
        attachData: uJSON,
        mediaType,
        masterId: contactId,
        error,
      });

      throw new Error(
        `DYNAMICS - Erro ao criar interação ou recuperar ticket: ${error instanceof Error ? error.message : error}`,
      );
    }
    return retorno;
  }

  public async cifUpdateTicket(
    attachData: any,
    contactId: string,
    mediaType: MediaType,
  ): Promise<Ticket> {
    let response: Ticket = {
      id_interacao: null,
      id_ticket: null,
    };
    this.cifDatadogService.updateTicket.start({
      mediaType,
      masterId: contactId,
      attachData,
    });

    try {
      const retorno = await this.cifSearchAndOpenRecords(contactId);

      if (!retorno?.id_interacao) {
        this.logger.warn(
          'DYNAMICS - id_interacao não encontrado, update não será executado, seguindo com a criação do ticket.',
        );

        this.cifDatadogService.updateTicket.notFound({
          masterId: contactId,
          mediaType,
          attachData,
        });

        return response;
      }

      const data = {
        statuscode: '1',
        statecode: '0',
        itau_mst_attachdata: JSON.stringify(attachData),
      };

      this.logger.info(
        `DYNAMICS - Dados do ticket que serão atualizados: ${JSON.stringify(data)}`,
      );

      // Atualiza o registro na entidade especificada com os dados fornecidos
      const result = await (window as any).Microsoft.CIFramework.updateRecord(
        this.tableName,
        retorno.id_interacao,
        JSON.stringify(data),
      );

      this.logger.info(`DYNAMICS - Update record return: ${result}`);

      response.id_interacao = retorno.id_interacao;
      response.id_ticket = retorno.id_ticket;

      this.cifDatadogService.updateTicket.success({
        masterId: contactId,
        mediaType,
        attachData,
      });

      return response;
    } catch (error) {
      this.cifDatadogService.updateTicket.fail({
        error,
        masterId: contactId,
        mediaType,
        attachData,
      });

      throw new Error(
        `DYNAMICS - Erro ao pesquisar o ticket aberto ou ao fazer o update record no Dynamics: ${error instanceof Error ? error.message : error}`,
      );
    }
  }

  private async cifRetrieveRecord(
    interactionId: string,
    tableName: string,
    mediaType: MediaType,
    masterId: string,
  ): Promise<string | null> {
    let idTicket = null;

    this.cifDatadogService.openTicket.retrieve.start({
      interactionId,
      mediaType,
      masterId,
    });

    for (let attempt = 0; attempt < this.RECALL_LIMIT; attempt++) {
      await this.delay(this.RECALL_DELAY_MS);

      const options = `?$select=itau_protocolo_interacao&$expand=${this.retrieveRecordTable}($select=${this.retrieveRecordCol})`;
      try {
        const result = await (
          window as any
        ).Microsoft.CIFramework.retrieveRecord(
          tableName,
          interactionId,
          options,
        );
        this.logger.info(`DYNAMICS - result retrieve: ${result}`);

        //const interaction = JSON.parse(result);
        let interaction = this.jsonConverterUtils.JsonParsed(result);

        // try {
        //   interaction = JSON.parse(result);
        // } catch {
        //   interaction = null;
        // }

        this.logger.info(
          `DYNAMICS - interaction: ${JSON.stringify(interaction)}`,
        );

        if (interaction && interaction[this.retrieveRecordTable]?.[0] != null) {
          idTicket =
            interaction[this.retrieveRecordTable][0][this.retrieveRecordCol];

          this.logger.info(
            `DYNAMICS - Ticket ID encontrado na tentativa ${attempt + 1}: ${idTicket}`,
          );

          this.cifDatadogService.openTicket.retrieve.success({
            interactionId,
            mediaType,
            masterId,
          });
          break;
        }

        this.logger.info(`DYNAMICS - Rechamada ${attempt + 1}`);
      } catch (error) {
        this.cifDatadogService.openTicket.retrieve.fail({
          interactionId,
          mediaType,
          masterId,
        });
        throw new Error(
          `Erro ao recuperar ticket na tentativa ${attempt + 1}: ${error instanceof Error ? error.message : error}`,
        );
      }
    }

    if (!idTicket) {
      this.logger.warn(
        `DYNAMICS - Limite de tentativas (${this.RECALL_LIMIT}) atingido ao recuperar o Ticket ID para a interação ${interactionId}`,
      );

      this.cifDatadogService.openTicket.retrieve.excessiveAttemptsError({
        interactionId,
        attempts: this.RECALL_LIMIT,
        mediaType,
        masterId,
      });
    }

    return idTicket;
  }

  private async cifSearchAndOpenRecords(contactId: string): Promise<Ticket> {
    let response: Ticket = {
      id_interacao: null,
      id_ticket: null,
    };
    const query = `?$select=itau_protocolo_interacao&$expand=${this.retrieveRecordTable}($select=${this.retrieveRecordCol})&$filter=itau_st_id_conexao eq '${contactId}'&$top=1`;

    try {
      const result = await (
        window as any
      ).Microsoft.CIFramework.searchAndOpenRecords(
        'itau_interacao',
        query,
        true,
      );

      //const parsed = JSON.parse(result);
      let parsed = this.jsonConverterUtils.JsonParsed(result);

      // try {
      //   parsed = JSON.parse(result);
      // } catch {
      //   parsed = {};
      // }
      const interaction = parsed && parsed['0'] ? parsed['0'] : null;

      this.logger.info(
        `DYNAMICS - interaction: ${JSON.stringify(interaction)}`,
      );

      if (
        interaction?.[this.retrieveRecordTable] &&
        interaction?.[this.retrieveRecordTable].length > 0 &&
        interaction?.[this.retrieveRecordTable][0]
      ) {
        response.id_ticket =
          interaction?.[this.retrieveRecordTable][0].incidentid;
      }

      if (interaction?.itau_interacaoid != null) {
        response.id_interacao = interaction.itau_interacaoid;
      }
    } catch (error) {
      this.logger.error(
        'DYNAMICS - Erro ao pesquisar o ticket na base ',
        error,
      );
      throw error;
    }

    return response;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // private safeJsonParse<T>(value: string, fallback: T): T {
  //   try {
  //     return JSON.parse(value) as T;
  //   } catch {
  //     return fallback;
  //   }
  // }
}
